import { centsToUnits } from '@systemeio/ui'
import LazyContactInfoTooltip from 'modules/contacts/components/lazy-contact-info-tooltip'
import { getFunnelStepUrl } from 'modules/funnels/funnel/utils/get-funnel-step-url'
import {
  MainEventsEnum,
  mainEventsDescription,
  mainEventsType,
} from 'modules/main/enums/main-events-enum'
import { MainEventType } from 'modules/main/types/main-interface'
import React from 'react'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import TransLoco from 'shared/components/trans-loco'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { currencyFormat } from 'shared/utils/currency-format'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { getBlogPostNextjsUrl } from 'shared/utils/get-blog-post-nextjs-url'
import { MoneyCancelIcon, MoneyReturnIcon, MoneySucceedIcon, PersonIcon } from '@systemeio/ui'

interface RenderEventProps {
  event: MainEventType
  lastItem?: boolean
}

const eventIcon: Record<MainEventsEnum, JSX.Element> = {
  [MainEventsEnum.contact_created]: <PersonIcon className={'h-[24px] w-[24px] fill-[#35E190]'} />,
  [MainEventsEnum.recurring_charge_succeed]: <MoneyReturnIcon />,
  [MainEventsEnum.one_shot_charge_succeed]: <MoneySucceedIcon />,
  [MainEventsEnum.payment_subscription_stopped]: <MoneyCancelIcon />,
  [MainEventsEnum.payment_subscription_completed]: <MoneyReturnIcon />,
  [MainEventsEnum.payment_subscription_created]: <MoneyReturnIcon />,
  [MainEventsEnum.recurring_charge_failed]: <MoneyCancelIcon />,
}

function RenderEvent({ event, lastItem }: RenderEventProps) {
  const { t } = useLocoTranslation()
  const { user } = useUser()

  const getEventElement = (): JSX.Element => {
    switch (event.type) {
      case MainEventsEnum.recurring_charge_succeed:
      case MainEventsEnum.one_shot_charge_succeed:
        return (
          <TransLoco
            t={t}
            i18nKey={mainEventsDescription[MainEventsEnum.recurring_charge_succeed]}
            values={{
              price: currencyFormat(
                centsToUnits(event.relatedObject.amount),
                event.relatedObject.payment.currency,
                user?.dashboardLocale,
              ),
              offer_name: event.relatedObject.orderItem.pricePlan
                ? event.relatedObject.orderItem.pricePlan.innerName
                : event.relatedObject.orderItem.productOrderItem
                ? event.relatedObject.orderItem.productOrderItem.product.name
                : '',
            }}
            components={{
              a:
                event.relatedObject.orderItem.offer &&
                event.relatedObject.orderItem.offer.funnelStep ? (
                  <LinkWithoutPrefetch
                    className="hover:text-text-tertiary/70 truncate underline"
                    href={getFunnelStepUrl(
                      event.relatedObject.orderItem.offer.funnelStep.funnel.id,
                      event.relatedObject.orderItem.offer.funnelStep.id,
                    )}
                  />
                ) : (
                  <span />
                ),
            }}
          />
        )
      case MainEventsEnum.recurring_charge_failed:
      case MainEventsEnum.payment_subscription_created:
      case MainEventsEnum.payment_subscription_stopped:
      case MainEventsEnum.payment_subscription_completed:
        return (
          <TransLoco
            t={t}
            i18nKey={mainEventsDescription[event.type]}
            values={{
              customer_name:
                event.relatedObject.orderItem.order.customer.fullName ||
                event.relatedObject.orderItem.order.customer.contact.connectedUser?.fullName ||
                event.relatedObject.orderItem.order.customer.contact.email,
              offer_name: event.relatedObject.orderItem.pricePlan
                ? event.relatedObject.orderItem.pricePlan.innerName
                : event.relatedObject.orderItem.productOrderItem
                ? event.relatedObject.orderItem.productOrderItem.product.name
                : '',
            }}
            components={{
              a: (
                <LazyContactInfoTooltip
                  contactId={event.relatedObject.orderItem.order.customer.contact.id}
                  removedAt={event.relatedObject.orderItem.order.customer.contact.removedAt}
                />
              ),
              b:
                event.relatedObject.orderItem.offer &&
                event.relatedObject.orderItem.offer.funnelStep ? (
                  <LinkWithoutPrefetch
                    className="hover:text-text-tertiary/70 truncate underline"
                    href={getFunnelStepUrl(
                      event.relatedObject.orderItem.offer.funnelStep.funnel.id,
                      event.relatedObject.orderItem.offer.funnelStep.id,
                    )}
                  />
                ) : (
                  <span />
                ),
            }}
          />
        )
      case MainEventsEnum.contact_created: {
        const funnelStep = event.relatedObject.sourcePage?.funnelStep && (
          <LinkWithoutPrefetch
            className="hover:text-text-tertiary/70 underline"
            href={getFunnelStepUrl(
              event.relatedObject.sourcePage.funnelStep.funnel.id,
              event.relatedObject.sourcePage.funnelStep.id,
            )}
          />
        )
        const blogPage = event.relatedObject.sourcePage?.blogPage && (
          <LinkWithoutPrefetch
            className="hover:text-text-tertiary/70 underline"
            href={getBlogPostNextjsUrl(event.relatedObject.sourcePage.blogPage.blog.id)}
          />
        )
        return (
          <TransLoco
            t={t}
            i18nKey={mainEventsDescription[MainEventsEnum.contact_created]}
            values={{
              email: event.relatedObject.email,
              form: event.relatedObject.pageTitle,
            }}
            components={{
              a: (
                <LazyContactInfoTooltip
                  contactId={event.relatedObject.id}
                  removedAt={event.relatedObject.removedAt}
                />
              ),
              b: event.relatedObject.sourcePage ? funnelStep || blogPage || <span /> : <span />,
            }}
          />
        )
      }
    }
  }
  return (
    <>
      <div className={`${lastItem ? 'pt-4' : 'py-[15px]'} font-averta flex gap-2.5`}>
        <div
          className={
            'flex h-[24px] min-w-[28px] items-center justify-center rounded-full [&>svg]:scale-75'
          }
        >
          {eventIcon[event.type]}
        </div>
        <div className={'flex w-full flex-col gap-1 truncate'}>
          <div className={'flex items-center justify-between gap-3'}>
            <span className={'font-bold text-[#414355]'}>{t(mainEventsType[event.type])}</span>
            <span className={'truncate text-xs text-[rgb(var(--text-secondary))]'}>
              {dateTimeFormat(event.createdAt, {
                timeFormat: user?.timeFormat,
                dateFormat: user?.dateFormat,
                timeZone: user?.timezone,
              })}
            </span>
          </div>
          <div className={'flex flex-wrap gap-1 truncate text-xs leading-normal'}>
            {getEventElement()}
          </div>
        </div>
      </div>
      {!lastItem && <hr className={'color-text-primary opacity-10'} />}
    </>
  )
}

export default RenderEvent
